import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import confetti from "canvas-confetti";
import { Sparkles, X, Share2 } from "lucide-react";

const CONFETTI_COLORS = ["#FFD600", "#FF5C00", "#FF3AC5", "#0047FF", "#00E676", "#FFFFFF"];

function fireConfetti() {
  const duration = 2500;
  const end = Date.now() + duration;
  (function frame() {
    confetti({
      particleCount: 4,
      angle: 60,
      spread: 65,
      startVelocity: 55,
      origin: { x: 0, y: 0.6 },
      colors: CONFETTI_COLORS,
      ticks: 200,
      scalar: 1.1,
    });
    confetti({
      particleCount: 4,
      angle: 120,
      spread: 65,
      startVelocity: 55,
      origin: { x: 1, y: 0.6 },
      colors: CONFETTI_COLORS,
      ticks: 200,
      scalar: 1.1,
    });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();
  // Big burst in the middle
  confetti({
    particleCount: 150,
    spread: 120,
    startVelocity: 45,
    origin: { x: 0.5, y: 0.45 },
    colors: CONFETTI_COLORS,
  });
}

export default function BadgeCelebrationModal({ badge, onClose }) {
  useEffect(() => {
    if (!badge) return;
    fireConfetti();
    // auto-celebrate haptic if available
    if (navigator.vibrate) {
      try { navigator.vibrate([30, 40, 30, 40, 80]); } catch {}
    }
  }, [badge]);

  if (!badge) return null;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4 animate-[fadeIn_200ms_ease-out]"
      onClick={onClose}
      data-testid="badge-celebration"
    >
      <div
        className="brutal bg-white max-w-md w-full p-8 text-center relative animate-[popIn_400ms_cubic-bezier(0.34,1.56,0.64,1)]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 w-9 h-9 border-2 border-black rounded-xl flex items-center justify-center bg-white hover:bg-neutral-100"
          aria-label="Close"
          data-testid="badge-celebration-close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-black bg-brand-yellow border-2 border-black rounded-full px-3 py-1 shadow-brutal">
          <Sparkles className="w-3.5 h-3.5" strokeWidth={2.8} /> Badge unlocked!
        </div>

        <div className="mt-5 relative">
          <div
            className="absolute inset-0 rounded-full blur-2xl opacity-50 animate-pulse"
            style={{ background: "radial-gradient(circle, #FFD600 0%, transparent 65%)" }}
          />
          {badge.icon_url && (
            <img
              src={badge.icon_url}
              alt={badge.name}
              crossOrigin="anonymous"
              className="relative w-40 h-40 mx-auto object-contain drop-shadow-[6px_6px_0_rgba(0,0,0,0.15)]"
            />
          )}
        </div>

        <h2 className="font-display font-black text-3xl mt-4 leading-none">{badge.name}</h2>
        <p className="text-sm text-neutral-700 mt-2">{badge.description}</p>

        <div className="flex gap-2 mt-6">
          <Link
            to="/app/badges"
            onClick={onClose}
            className="btn-brutal btn-white flex-1 text-sm"
            data-testid="badge-celebration-see-all"
          >
            <Share2 className="w-4 h-4" /> See all
          </Link>
          <button
            type="button"
            onClick={onClose}
            className="btn-brutal btn-primary flex-1 text-sm"
            data-testid="badge-celebration-ok"
          >
            <Sparkles className="w-4 h-4" /> Awesome!
          </button>
        </div>

        <p className="text-[11px] text-neutral-500 mt-4 italic">
          Keep referring to unlock more — 20 badges total.
        </p>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes popIn {
          0%   { transform: scale(0.6) rotate(-4deg); opacity: 0; }
          60%  { transform: scale(1.08) rotate(1deg); opacity: 1; }
          100% { transform: scale(1) rotate(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
